import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import AlertBox from "../components/AlertBox";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaImage, FaBoxOpen } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const API_BASE = "https://blog.wageh.net";

export default function AdminProducts() {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editingId, setEditingId] = useState(null);
    const [categoryId, setCategoryId] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");
    const [stock, setStock] = useState("");
    const [imageFile, setImageFile] = useState(null);

    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);

    const catMap = useMemo(() => {
        const m = new Map();
        cats.forEach((c) => m.set(c.id, c.name));
        return m;
    }, [cats]);

    const resetForm = () => {
        setEditingId(null);
        setCategoryId("");
        setName("");
        setDescription("");
        setPrice("");
        setStock("");
        setImageFile(null);
    };

    const fetchCats = async () => {
        try {
            const res = await api.get("/admin/categories");
            const payload = res.data.data ?? res.data;
            setCats(payload.data ?? payload);
        } catch {}
    };

    const fetchProducts = async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await api.get("/admin/products");
            const payload = res.data.data ?? res.data;
            setProducts(payload.data ?? payload);
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_prod_load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            await fetchCats();
            await fetchProducts();
        })();
    }, []);

    const startCreate = () => {
        setMsg("");
        setErr("");
        resetForm();
    };

    const startEdit = async (p) => {
        setMsg("");
        setErr("");
        setBusy(true);

        try {
            const res = await api.get(`/admin/products/${p.id}`);
            const payload = res.data.data ?? res.data;
            const prod = payload.data ?? payload;

            setEditingId(prod.id);
            setCategoryId(String(prod.category_id ?? prod.category?.id ?? ""));
            setName(prod.name ?? "");
            setDescription(prod.description ?? "");
            setPrice(String(prod.price ?? ""));
            setStock(String(prod.stock ?? ""));
            setImageFile(null);
            
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_prod_details_failed"));
        } finally {
            setBusy(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setMsg("");
        setBusy(true);

        try {
            const body = {
                category_id: Number(categoryId),
                name: name.trim(),
                description: description.trim(),
                price: Number(price),
                stock: Number(stock),
            };

            let savedId = editingId;

            if (editingId) {
                await api.put(`/admin/products/${editingId}`, body);
                setMsg(t("admin_prod_update_success"));
            } else {
                const res = await api.post("/admin/products", body);
                const payload = res.data.data ?? res.data;
                const created = payload.data ?? payload;
                savedId = created.id;
                setMsg(t("admin_prod_create_success"));
            }

            if (imageFile && savedId) {
                const form = new FormData();
                form.append("image", imageFile);

                await api.post(`/admin/products/${savedId}/image`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                setMsg((m) => (m ? m + " + " + t("admin_prod_image_success") : t("admin_prod_image_success")));
            }

            resetForm();
            fetchProducts();
        } catch (e) {
            const data = e.response?.data;
            if (data?.errors) {
                const firstKey = Object.keys(data.errors)[0];
                setErr(data.errors[firstKey][0]);
            } else {
                setErr(data?.message || t("admin_prod_save_failed"));
            }
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        const ok = window.confirm(t("admin_prod_confirm_delete"));
        if (!ok) return;

        setBusy(true);
        setMsg("");
        setErr("");

        try {
            await api.delete(`/admin/products/${id}`);
            setMsg(t("admin_prod_delete_success"));
            fetchProducts();
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_prod_delete_failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="container-fluid p-0 pb-5">
            <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                <div className="d-flex align-items-center">
                    <FaBoxOpen className="fs-3 text-primary me-3" />
                    <h2 className="m-0 fw-bold text-dark">{t("admin_prod_title")}</h2>
                </div>
                <button className="btn btn-primary shadow-sm fw-bold px-4" onClick={startCreate}>
                    <FaPlus className="me-2" /> {t("admin_prod_add")}
                </button>
            </div>

            <AlertBox type="success" message={msg} onClose={() => setMsg("")} />
            <AlertBox type="danger" message={err} onClose={() => setErr("")} />

            <div className="card shadow-sm mb-5 border-0 rounded-4 overflow-hidden">
                <div className={`card-header text-white pt-3 pb-2 ${editingId ? 'bg-warning text-dark' : 'bg-primary'}`}>
                    <h5 className="m-0 fw-bold">
                        {editingId ? (
                            <><FaEdit className="me-2" /> {t("admin_prod_edit")}{editingId}</>
                        ) : (
                            <><FaPlus className="me-2" /> {t("admin_prod_new")}</>
                        )}
                    </h5>
                </div>
                
                <div className="card-body p-4 bg-light">
                    <form onSubmit={submit} className="row g-4">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">{t("admin_prod_category")} <span className="text-danger">*</span></label>
                            <select className="form-select shadow-none" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
                                <option value="">{t("admin_prod_select_cat")}</option>
                                {cats.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-bold">{t("admin_prod_name")} <span className="text-danger">*</span></label>
                            <input className="form-control shadow-none" placeholder={t("admin_prod_name_ph")} value={name} onChange={(e) => setName(e.target.value)} required />
                        </div>

                        <div className="col-12">
                            <label className="form-label fw-bold">{t("admin_prod_desc")}</label>
                            <textarea className="form-control shadow-none" rows={3} placeholder={t("admin_prod_desc_ph")} value={description} onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        <div className="col-md-4">
                            <label className="form-label fw-bold">{t("admin_prod_price")} <span className="text-danger">*</span></label>
                            <input type="number" className="form-control shadow-none" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} required />
                        </div>

                        <div className="col-md-4">
                            <label className="form-label fw-bold">{t("admin_prod_stock")} <span className="text-danger">*</span></label>
                            <input type="number" className="form-control shadow-none" placeholder={t("admin_prod_qty_ph")} value={stock} onChange={(e) => setStock(e.target.value)} required />
                        </div>

                        <div className="col-md-4">
                            <label className="form-label fw-bold">{t("admin_prod_image")}</label>
                            <input type="file" className="form-control shadow-none" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                            <small className="text-muted d-block mt-2" style={{ fontSize: "0.85rem" }}>
                                {editingId ? t("admin_prod_image_edit_hint") : t("admin_prod_image_new_hint")}
                            </small>
                        </div>

                        <div className="col-12 mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                            {editingId && (
                                <button type="button" className="btn btn-outline-secondary px-4 fw-bold shadow-sm" onClick={resetForm} disabled={busy}>
                                    <FaTimes className="me-2" /> {t("admin_prod_cancel")}
                                </button>
                            )}
                            <button className="btn btn-success px-5 fw-bold shadow-sm" disabled={busy}>
                                {busy ? <span className="spinner-border spinner-border-sm me-2"></span> : <FaSave className="me-2" />}
                                {busy ? t("admin_prod_saving") : t("admin_prod_save")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header bg-white pt-4 pb-0 border-bottom-0">
                    <h5 className="fw-bold m-0">{t("admin_prod_available")}</h5>
                </div>
                
                <div className="card-body p-0 mt-3">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                        </div>
                    ) : products.length === 0 ? (
                        <div className="alert alert-info m-4 text-center py-5 rounded-3">
                            <FaBoxOpen className="display-4 mb-3 opacity-50" />
                            <h5 className="m-0">{t("admin_prod_no_found")}</h5>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle text-center mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="py-3" style={{ width: "70px" }}>{t("admin_prod_col_id")}</th>
                                        <th className="py-3" style={{ width: "100px" }}>{t("admin_prod_col_image")}</th>
                                        <th className="py-3 text-start ps-3">{t("admin_prod_col_name")}</th>
                                        <th className="py-3">{t("admin_prod_col_category")}</th>
                                        <th className="py-3">{t("admin_prod_col_price")}</th>
                                        <th className="py-3">{t("admin_prod_col_stock")}</th>
                                        <th className="py-3" style={{ width: "180px" }}>{t("admin_prod_col_actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p) => {
                                        const cleanPath = p.image ? p.image.replace(/^\/+/, '').replace(/^storage\//, '').replace(/^uploads\//, '') : "";
                                        const imgSrc = p.image ? (p.image.startsWith("http") ? p.image : `${API_BASE}/uploads/${cleanPath}`) : null;

                                        return (
                                            <tr key={p.id}>
                                                <td className="fw-bold text-muted">#{p.id}</td>
                                                <td>
                                                    {imgSrc ? (
                                                        <img src={imgSrc} alt={p.name} className="rounded shadow-sm" style={{ width: 50, height: 50, objectFit: "contain", backgroundColor: "#fff" }} />
                                                    ) : (
                                                        <div className="bg-light d-inline-flex align-items-center justify-content-center rounded shadow-sm" style={{ width: 50, height: 50 }}>
                                                            <FaImage className="text-muted" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-start ps-3 fw-bold text-dark fs-6">{p.name}</td>
                                                <td>
                                                    <span className="badge bg-secondary px-3 py-2 rounded-pill">
                                                        {catMap.get(p.category_id) || p.category?.name || "-"}
                                                    </span>
                                                </td>
                                                <td className="text-success fw-bold">${p.price}</td>
                                                <td>
                                                    <span className={`badge bg-${Number(p.stock) > 0 ? "success" : "danger"} px-3 py-2 rounded-pill`}>
                                                        {Number(p.stock) > 0 ? `${p.stock} ${t("admin_prod_in_stock")}` : t("admin_prod_out_of_stock")}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button className="btn btn-outline-primary btn-sm px-3 rounded-pill" onClick={() => startEdit(p)} disabled={busy}>
                                                            <FaEdit />
                                                        </button>
                                                        <button className="btn btn-outline-danger btn-sm px-3 rounded-pill" onClick={() => remove(p.id)} disabled={busy}>
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}